import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, MapPin, Image } from 'lucide-react'
import { getHomes, createHome, updateHome, deleteHome } from '../lib/supabase'
import { Modal, Field, Input, Textarea, ConfirmDialog, EmptyState, Loading, DotMenu, formatCurrency } from '../components/ui'
import { useSettings } from '../lib/settings'

function HomeForm({ initial = {}, onSave, onClose }) {
  const [form, setForm] = useState({
    name: '', street_address: '', city: '', state: '', zip: '',
    year_built: '', square_footage: '', image_url: '', notes: '',
    ...initial
  })
  const [saving, setSaving] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const handleSave = async () => {
    if (!form.name.trim()) return
    setSaving(true)
    try { await onSave(form) } finally { setSaving(false) }
  }

  return (
    <Modal title={initial.id ? 'Edit Property' : 'Add Property'} onClose={onClose} size="lg"
      footer={<>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.name.trim()}>
          {saving ? 'Saving…' : 'Save Property'}
        </button>
      </>}>
      <Field label="Property Image" hint="Paste a URL to a photo or Google Drive image">
        <div className="image-upload-hint" style={{ marginBottom: 6 }}>
          <Image size={14} /> Paste image URL below
        </div>
        <Input placeholder="https://…" value={form.image_url} onChange={e => set('image_url', e.target.value)} />
      </Field>
      <Field label="Property Name *">
        <Input placeholder="e.g. Beach House" value={form.name} onChange={e => set('name', e.target.value)} />
      </Field>
      <Field label="Street Address">
        <Input placeholder="123 Coastal Ave" value={form.street_address} onChange={e => set('street_address', e.target.value)} />
      </Field>
      <div className="form-row-3">
        <Field label="City"><Input placeholder="Wilmington" value={form.city} onChange={e => set('city', e.target.value)} /></Field>
        <Field label="State"><Input placeholder="NC" value={form.state} onChange={e => set('state', e.target.value)} /></Field>
        <Field label="Zip"><Input placeholder="28412" value={form.zip} onChange={e => set('zip', e.target.value)} /></Field>
      </div>
      <div className="form-row">
        <Field label="Year Built"><Input type="number" placeholder="2005" value={form.year_built} onChange={e => set('year_built', e.target.value)} /></Field>
        <Field label="Square Footage"><Input type="number" placeholder="1800" value={form.square_footage} onChange={e => set('square_footage', e.target.value)} /></Field>
      </div>
      <Field label="Notes"><Textarea placeholder="General notes…" value={form.notes} onChange={e => set('notes', e.target.value)} /></Field>
    </Modal>
  )
}

function HomeCard({ home, onClick, onEdit, onDelete }) {
  const { showHomeTotals } = useSettings()
  const items = home.items || []
  const committed = items.filter(i => i.status === 'Purchased' || i.status === 'Installed').reduce((s, i) => s + (i.cost || 0), 0)

  return (
    <div className="card" style={{ cursor: 'pointer' }} onClick={onClick}>
      {home.image_url ? (
        <div style={{ height: 160, borderRadius: '12px 12px 0 0', overflow: 'hidden' }}>
          <img src={home.image_url} alt={home.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.parentElement.style.display = 'none' }} />
        </div>
      ) : (
        <div style={{ height: 100, borderRadius: '12px 12px 0 0', background: 'var(--navy-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ocean)', fontSize: '.8125rem', gap: 6 }}>
          <Image size={14} /> No image
        </div>
      )}
      <DotMenu onEdit={onEdit} onDelete={onDelete} />
      <div className="card-body">
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.0625rem', letterSpacing: '-.01em', marginBottom: 4 }}>{home.name}</h3>
        {(home.street_address || home.city) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--ocean)', fontSize: '.75rem' }}>
            <MapPin size={12} />
            {[home.street_address, home.city, home.state].filter(Boolean).join(', ')}
          </div>
        )}
        {showHomeTotals && committed > 0 && (
          <div style={{ marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: '.8125rem', color: 'var(--navy)', fontWeight: 500 }}>
            {formatCurrency(committed)} committed
          </div>
        )}
      </div>
    </div>
  )
}

export default function HomesPage() {
  const navigate = useNavigate()
  const [homes, setHomes] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState(null)
  const [deleting, setDeleting] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const load = async () => {
    try { setHomes(await getHomes()) } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  const handleSave = async (form) => {
    const payload = {
      ...form,
      year_built: form.year_built ? parseInt(form.year_built, 10) : null,
      square_footage: form.square_footage ? parseInt(form.square_footage, 10) : null,
    }
    try {
      editing ? await updateHome(editing.id, payload) : await createHome(payload)
      setShowForm(false); setEditing(null); load()
    } catch (err) {
      console.error('Error saving property:', err)
      alert(`Error saving property: ${err.message}`)
    }
  }

  const handleDelete = async () => {
    setDeleteLoading(true)
    try { await deleteHome(deleting.id); setDeleting(null); load() }
    finally { setDeleteLoading(false) }
  }

  return (
    <div className="page-body">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Properties</h1>
          <p className="page-subtitle">Manage your homes and track everything inside them</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          <Plus size={15} /> Add Property
        </button>
      </div>

      {loading ? <Loading /> : homes.length === 0 ? (
        <EmptyState icon="🏠" title="No properties yet" description="Add your first property to start tracking rooms, items, and expenses."
          action={<button className="btn btn-primary" onClick={() => setShowForm(true)}><Plus size={15} /> Add Property</button>} />
      ) : (
        <div className="grid-homes">
          {homes.map(home => (
            <HomeCard key={home.id} home={home}
              onClick={() => navigate(`/homes/${home.id}`)}
              onEdit={() => { setEditing(home); setShowForm(true) }}
              onDelete={() => setDeleting(home)} />
          ))}
        </div>
      )}

      {showForm && <HomeForm initial={editing || {}} onSave={handleSave} onClose={() => { setShowForm(false); setEditing(null) }} />}
      {deleting && <ConfirmDialog title="Delete Property" message={`Delete "${deleting.name}"? All rooms, items, and expenses will also be deleted.`} onConfirm={handleDelete} onCancel={() => setDeleting(null)} loading={deleteLoading} />}
    </div>
  )
}
